import { useCallback, useEffect, useState } from 'react';
import { TIMED_POPUP_STORAGE_KEY } from './constants';
import datetime from 'lib/datetime';
import useSyncedStorageState from 'hooks/useSyncedStorageState';

interface TimedPopupOptions {
  timeThreshold?: number;
  minProductViews?: number;
  isCartEmpty?: boolean;
  isNewUser?: boolean;
  shouldTrack?: boolean;
}

interface EngagementBasedPopupState {
  startTime: number | null;
  viewedProductIds: string[];
  dismissed: boolean;
  popupTriggered: boolean;
}

const defaultState: EngagementBasedPopupState = {
  startTime: null,
  viewedProductIds: [],
  dismissed: false,
  popupTriggered: false,
};

const useEngagementBasedPopup = ({
  timeThreshold = 45_000,
  minProductViews = 2,
  isCartEmpty,
  isNewUser,
  shouldTrack,
}: TimedPopupOptions = {}) => {
  const { state, update, hydrated } = useSyncedStorageState(
    TIMED_POPUP_STORAGE_KEY,
    defaultState,
    {
      channel: 'engagement-popup',
      lockKey: 'engagement-popup-lock',
    },
  );

  const [showPopup, setShowPopup] = useState(false);

  // Initialize startTime & time-based trigger
  useEffect(() => {
    if (
      !hydrated ||
      !shouldTrack ||
      !isCartEmpty ||
      !isNewUser ||
      state.dismissed ||
      state.popupTriggered ||
      state.startTime !== null
    )
      return;

    update((prev) => ({
      ...prev,
      startTime: datetime().valueOf(),
    }));
  }, [
    hydrated,
    isCartEmpty,
    isNewUser,
    shouldTrack,
    state.dismissed,
    state.popupTriggered,
    state.startTime,
    update,
  ]);

  // Time-based trigger
  useEffect(() => {
    if (
      !hydrated ||
      !shouldTrack ||
      !isCartEmpty ||
      !isNewUser ||
      state.popupTriggered ||
      state.dismissed ||
      state.startTime === null
    )
      return;

    const elapsed = Date.now() - state.startTime;
    const remaining = Math.max(timeThreshold - elapsed, 0);

    const timer = setTimeout(() => {
      update((prev) => ({ ...prev, popupTriggered: true }));
    }, remaining);

    return () => clearTimeout(timer);
  }, [
    hydrated,
    isCartEmpty,
    isNewUser,
    shouldTrack,
    state.dismissed,
    state.popupTriggered,
    state.startTime,
    timeThreshold,
    update,
  ]);

  // Trigger popup
  useEffect(() => {
    if (
      state.popupTriggered &&
      !state.dismissed &&
      isCartEmpty &&
      isNewUser &&
      shouldTrack
    ) {
      const timer = setTimeout(() => {
        setShowPopup(true);
      }, 1000);

      return () => clearTimeout(timer);
    } else {
      setShowPopup(false);
    }
  }, [
    isCartEmpty,
    isNewUser,
    shouldTrack,
    state.dismissed,
    state.popupTriggered,
  ]);

  const trackProductView = useCallback(
    (productId: string) => {
      if (!hydrated || !shouldTrack || !isCartEmpty || !isNewUser)
        return;

      update((prev) => {
        if (prev.dismissed || prev.popupTriggered) return prev;

        if (prev.viewedProductIds.includes(productId)) return prev;

        const updatedIds = [...prev.viewedProductIds, productId];

        return {
          ...prev,
          viewedProductIds: updatedIds,
          popupTriggered:
            updatedIds.length >= minProductViews
              ? true
              : prev.popupTriggered,
        };
      });
    },
    [
      hydrated,
      isCartEmpty,
      isNewUser,
      minProductViews,
      shouldTrack,
      update,
    ],
  );

  const dismiss = useCallback(() => {
    update((prev) => ({ ...prev, dismissed: true }));
    setShowPopup(false);
  }, [update]);

  return { showPopup, trackProductView, dismiss };
};

export default useEngagementBasedPopup;
